import React, { useCallback, useEffect, useRef, useState } from "react";
import { CustomBreadcrumb } from "../../../shared/components/custom-breadcrumb/custom-breadcrumb";
import { CustomTable } from "../../../shared/components/custom-table/custom-table";
import { SERVICES } from "../../../core/services/services";
import { CustomLoader } from "../../../shared/components/custom-loader/custom-loader";
import { CustomModal } from "../../../shared/components/custom-modal/custom-modal";
import { CustomConfirmDialog } from "../../../shared/components/custom-confirm-dialog/custom-confirm-dialog";
import { Toast } from "primereact/toast";
import { HELPER } from "../../../shared/helper/helper";
import ErrorBoundary from "../../../shared/components/error-boundary/error-boundary";
import { API } from "../../../core/apis/apis";
import { MerchantCategoryForm } from "./merchant-category-form";

export function MerchantCategories() {
  const toast = useRef(null);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(1);
  const [error, setError] = useState("");
  const [modalLoading, setModalLoading] = useState(false);
  const [itemIdForDelete, setItemIdForDelete] = useState("");
  const [merchantCategoryForEdit, setMerchantCategoryForEdit] = useState("");

  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [range] = useState(5);
  const [emptyText, setEmptyText] = useState("");
  const [currentModalIndex, setCurrentModalIndex] = useState(0);
  const [pageNo, setPageNo] = useState(0);
  const [merchantCategories, setMerchantCategories] = useState([]);
  const [confirmText, setConfirmText] = useState("");
  const [successMessage, setSuccessMessage] = useState(null);
  const [isCategoryUpdate, setIsCategoryUpdate] = useState(false);
  const tableHeaders = [
    { label: "Name", value: "name" },
    { label: "Code", value: "code" },
  ];
  if (HELPER.HAS_AUTHORITY("bo_dd_manage_merchant_category")) {
    tableHeaders.push({ label: "Actions", value: "actions" });
  }

  const authorities = [
    { label: "DELETE", value: "bo_dd_manage_merchant_category" },
    { label: "UPDATE", value: "bo_dd_manage_merchant_category" },
  ];

  function deleteMerchantCategory(e) {
    setModalLoading(true);
    const url = `${API.MERCHANT_CATEGORIES}/${itemIdForDelete}`;
    SERVICES.MAKE_DELETE_REQUEST(url)
      .then((data) => {
        setSuccessMessage("Merchant category has been deleted!");
        setModalLoading(false);
      })
      .catch((error) => {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: HELPER.PROCESS_ERROR(error, "TOAST"),
          life: 10000,
        });
        setModalLoading(false);
      });
  }

  const openAction = useCallback((e, action, isMobile) => {
    // eslint-disable-next-line default-case
    switch (action) {
      case "DELETE":
        setSuccessMessage(null);
        setItemIdForDelete(e?.code);
        setConfirmText(`${e?.code}`);
        openModal(1, isMobile);
        break;
      case "UPDATE":
        setMerchantCategoryForEdit(e);
        setIsCategoryUpdate(true);
        openModal(0);
        break;
    }
  }, []);

  function goToPage(pageNo) {
    setPageNo(pageNo);
  }

  const getMerchantCategories = useCallback(() => {
    setCurrentIndex(0);
    setLoading(true);
    setMerchantCategories([]);
    const params = HELPER.TO_URL_STRING({
      page: pageNo,
      size: 10,
    });
    SERVICES.MAKE_GET_REQUEST(API.MERCHANT_CATEGORIES, params)
      .then((data) => {
        const result = data?.result?.content;
        if (!result.length) {
          setEmptyText("No merchant categories found ...");
        } else {
          let arr = [];
          setTotalItems(data?.result.totalElements);
          setTotalPages(data?.result.totalPages);
          result.forEach((e) => {
            arr.push({
              ...e,
              actions: "UD",
              detailsFunction: openAction,
            });
          });
          setMerchantCategories(arr);
        }
        setError(null);
        setCurrentIndex(1);
        setLoading(false);
      })
      .catch((error) => {
        setError(HELPER.PROCESS_ERROR(error));
        setCurrentIndex(1);
        setLoading(false);
      });
  }, [pageNo, openAction]);

  useEffect(() => {
    getMerchantCategories();
  }, [getMerchantCategories]);

  function onHide() {}

  function openModal(index, isMobile) {
    setCurrentModalIndex(index);

    setVisible(true);
  }

  function reload(resetPageNo = false) {
    setLoading(true);
    setCurrentIndex(0);
    if (resetPageNo) {
      setPageNo(0);
    }
    getMerchantCategories();
  }

  function closeModal(shouldReload, resetPageNo) {
    if (visible) {
      setVisible(false);
    }
    if (shouldReload === true) {
      reload(resetPageNo);
    }
  }

  const renderTableView = () => {
    // eslint-disable-next-line default-case
    switch (currentIndex) {
      case 0:
        return (
          <div className="loading-container">
            <CustomLoader loadingText="Loading merchant categories..." />
          </div>
        );
      case 1:
        return (
          <CustomTable
            isReload={true}
            // hasPagination={false}
            authorities={authorities}
            totalPages={totalPages}
            totalItems={totalItems}
            currentPage={pageNo + 1}
            range={range}
            emptyText={emptyText}
            search={true}
            reload={reload}
            error={error}
            items={merchantCategories}
            headers={tableHeaders}
            nextPage={() => setPageNo(pageNo + 1)}
            prevPage={() => setPageNo(pageNo - 1)}
            goToFirstPage={() => setPageNo(0)}
            goToLastPage={() => setPageNo(totalPages - 1)}
            goToPage={goToPage}
          />
        );
    }
  };

  const modalContent = () => {
    // eslint-disable-next-line default-case
    switch (currentModalIndex) {
      case 0:
        return (
          <MerchantCategoryForm
            closeModal={closeModal}
            merchantCategory={merchantCategoryForEdit}
            isUpdate={isCategoryUpdate}
          />
        );
      case 1:
        return (
          <CustomConfirmDialog
            itemId={itemIdForDelete}
            loading={modalLoading}
            loadingText="Deleting merchant category..."
            fn={deleteMerchantCategory}
            closeModal={closeModal}
            confirmText={confirmText}
            success={successMessage}
          />
        );
    }
  };

  return (
    <ErrorBoundary>
      <div>
        <Toast ref={toast} />
        <div>
          <CustomModal
            closeModal={closeModal}
            onHide={onHide}
            visible={visible}
            modalContent={modalContent}
          />
        </div>
        <div className="page-title p-text-left">Merchant Categories</div>
        <div className="p-mt-2">
          <CustomBreadcrumb page={"Manage Merchant Categories"} />
        </div>
        <div className="floating-buttons desktop-scree">
          <div className="p-grid">
            <div className="p-col-7"></div>
            <div className="p-col-5">
              <div className="p-grid">
                <div className="p-col-6 p-d-flex p-jc-end p-ai-center"></div>
                <div className="p-col-6">
                  <div
                    className={
                      HELPER.HAS_AUTHORITY("bo_dd_manage_merchant_category")
                        ? "dcir-show"
                        : "dcir-hide"
                    }
                  >
                    <button
                      disabled={loading}
                      onClick={() => {
                        setIsCategoryUpdate(false);

                        openModal(0, false);
                      }}
                      className="primary-button hide-btn-text text-small"
                    >
                      <i className="pi pi-plus" />
                      <span className="p-pl-2 hide-btn-text">
                        New merchant category
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="p-mt-3">{renderTableView()}</div>
      </div>
    </ErrorBoundary>
  );
}
